import { useRef, useEffect } from "react";
import { Canvas } from "@react-three/fiber";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import CosmicPortal from "@/components/three/CosmicPortal";

const LINES = ["The Architecture", "of Matriarchal", "Power."];

const ease = [0.22, 1, 0.36, 1];

export const Hero = ({ onEnter }) => {
  const mouse = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const handle = (e) => {
      mouse.current = {
        x: (e.clientX / window.innerWidth) * 2 - 1,
        y: -(e.clientY / window.innerHeight) * 2 + 1,
      };
    };
    window.addEventListener("mousemove", handle);
    return () => window.removeEventListener("mousemove", handle);
  }, []);

  return (
    <section id="hero" className="relative h-[100svh] w-full overflow-hidden" data-testid="hero-section">
      {/* radial glow bloom */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[70vh] w-[70vh] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#3B2A48]/25 blur-[120px]" />

      <div className="absolute inset-0">
        <Canvas
          camera={{ position: [0, 0, 6], fov: 45 }}
          dpr={[1, 2]}
          gl={{ antialias: true, alpha: true }}
        >
          <CosmicPortal mouse={mouse} />
        </Canvas>
      </div>

      <div className="pointer-events-none relative z-10 mx-auto flex h-full max-w-[1600px] flex-col justify-center px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 1 }}
          className="overline mb-6"
        >
          Sybils Society — The Return of the Matriarchs
        </motion.p>

        <h1 className="font-serif text-[clamp(2.6rem,9vw,7.5rem)] font-light leading-[0.95] tracking-tight text-white">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ delay: 0.35 + i * 0.16, duration: 1.05, ease }}
              >
                {i === 1 ? (
                  <span className="italic text-[#C5A059]">{line}</span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 1, ease }}
          className="mt-8 max-w-md text-base font-light leading-relaxed text-white/60"
        >
          An ecosystem of intuitive wisdom, cyclical rhythm, and legacy. Lead from
          the root. Rise from the lineage.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 1, ease }}
          className="pointer-events-auto mt-12"
        >
          <button
            onClick={onEnter}
            className="cta-glow group relative overflow-hidden rounded-full bg-[#C5A059] px-9 py-4 text-xs font-medium uppercase tracking-[0.24em] text-[#050505] transition-transform duration-300 hover:-translate-y-1"
            data-testid="hero-enter-council-btn"
          >
            <span className="relative z-10">Enter the Council</span>
          </button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="pointer-events-none absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3 text-white/40"
      >
        <span className="overline text-[0.6rem]">Scroll to ascend</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown strokeWidth={1} size={18} />
        </motion.span>
      </motion.div>
    </section>
  );
};

export default Hero;
